/**
 * Page de suivi de livraison, côté client.
 *
 * Les informations viennent d'une route interrogée périodiquement
 * (`useDeliveryTracking`) et non plus d'un canal temps réel : ce canal
 * n'était alimenté par personne, et la page affichait « Connecté — suivi en
 * temps réel actif » devant une carte vide. Les bandeaux de connexion ont
 * disparu avec lui — il n'y a plus de connexion à annoncer, seulement des
 * informations plus ou moins fraîches.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDeliveryTracking } from '../../hooks/useDeliveryTracking';
import DeliveryTrackingMap from '../../components/delivery/DeliveryTrackingMap';
import { ecommerceApi } from '../../api/ecommerce.api';
import { logger } from '../../utils/logger';
import type { Order } from '../../types/ecommerce.types';

/** « 14:35 » à partir d'une date ISO ; rien si la date est absente ou illisible. */
function heure(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' });
}

/** « il y a 2 min » — le client doit pouvoir juger de la fraîcheur de la position. */
function fraicheur(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const minutes = Math.round((Date.now() - d.getTime()) / 60000);
  if (minutes <= 0) return "à l'instant";
  if (minutes === 1) return 'il y a 1 minute';
  return `il y a ${minutes} minutes`;
}

export default function DeliveryTrackingPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { tracking, error: trackingError, refresh } = useDeliveryTracking(orderId || null);

  // Charger les détails de la commande
  useEffect(() => {
    if (!orderId) {
      setError('ID de commande manquant');
      setLoading(false);
      return;
    }

    loadOrder();
  }, [orderId]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const data = await ecommerceApi.orders.getOrderById(orderId!);
      setOrder(data);
      setError(null);
    } catch (err: any) {
      logger.error('Erreur chargement commande:', err);
      setError(err.response?.data?.message || 'Impossible de charger la commande');
    } finally {
      setLoading(false);
    }
  };

  // La destination vient du suivi (adresse géocodée côté serveur) ; l'adresse de
  // la commande sert de repli pour les adresses saisies avant le géocodage.
  const getCustomerLocation = (): [number, number] | null => {
    if (tracking?.destination) {
      return [tracking.destination.lat, tracking.destination.lng];
    }

    const address = order?.delivery_address;
    if (
      address &&
      typeof address.latitude === 'number' &&
      typeof address.longitude === 'number'
    ) {
      return [address.latitude, address.longitude];
    }

    return null;
  };

  const customerLocation = getCustomerLocation();
  const customerAddress = order?.delivery_address
    ? `${order.delivery_address.street}, ${order.delivery_address.postal_code} ${order.delivery_address.city}`
    : undefined;

  const driverPos: [number, number] | null = tracking?.driver_position
    ? [tracking.driver_position.lat, tracking.driver_position.lng]
    : null;

  const livree = tracking?.state === 'delivered';
  const enRoute = tracking?.state === 'en_route';
  const heurePrevue = heure(tracking?.estimated_time);
  const heureReelle = heure(tracking?.actual_time);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center max-w-md">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 text-xl">Chargement du suivi...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center max-w-md">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Erreur</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/shop/orders')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors"
          >
            Retour aux commandes
          </button>
        </div>
      </div>
    );
  }

  // Vérifier que c'est une commande en livraison
  if (order.delivery_option !== 'delivery') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-yellow-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center max-w-md">
          <div className="text-6xl mb-4">🏪</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Commande en retrait magasin
          </h1>
          <p className="text-gray-600 mb-6">
            Cette commande est à retirer en magasin. Le suivi de tournée ne concerne que les livraisons.
          </p>
          <button
            onClick={() => navigate('/shop/orders')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors"
          >
            Retour aux commandes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 pb-8">
      {/* Header */}
      <div className="bg-white shadow-md sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/shop/orders')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Retour
            </button>

            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900">
                Suivi de livraison
              </h1>
              <p className="text-gray-600">Commande #{order.order_number}</p>
            </div>

            <button
              onClick={refresh}
              className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium"
              title="Actualiser maintenant"
            >
              Actualiser
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {trackingError && (
          <div className="bg-orange-50 border border-orange-300 rounded-xl p-4 mb-6 text-orange-800">
            {trackingError}
          </div>
        )}

        {/* Pas encore dans une tournée : on le dit, plutôt qu'une carte vide. */}
        {tracking?.state === 'not_scheduled' && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 text-center">
            <div className="text-5xl mb-3">🗓️</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Livraison pas encore planifiée</h2>
            <p className="text-gray-600">
              {tracking.message || "Votre commande n'est pas encore affectée à une tournée."}
            </p>
          </div>
        )}

        {/* Livraison terminée */}
        {livree && (
          <div className="bg-gradient-to-r from-green-100 to-green-50 border-2 border-green-500 rounded-xl p-6 mb-6">
            <div className="text-center">
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-3xl font-bold text-green-700 mb-2">
                Livraison effectuée !
              </h2>
              <p className="text-gray-700 mb-4">
                Votre commande a été livrée{heureReelle ? ` à ${heureReelle}` : ''}.
              </p>
              {tracking?.proof?.recipient_name && (
                <p className="text-sm text-gray-600">
                  Reçu par : <span className="font-bold">{tracking.proof.recipient_name}</span>
                </p>
              )}
              {tracking?.proof?.notes && (
                <p className="text-sm text-gray-600 mt-2 italic">
                  Note : {tracking.proof.notes}
                </p>
              )}
            </div>
          </div>
        )}

        {tracking?.incident && !livree && (
          <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 mb-6 text-red-800">
            <div className="font-bold">La livraison a rencontré un problème</div>
            <div className="text-sm mt-1">
              Nous vous recontactons. Vous pouvez aussi nous appeler au magasin.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Carte (2/3 de l'espace) */}
          <div className="lg:col-span-2">
            {customerLocation ? (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden" style={{ height: '600px' }}>
                <DeliveryTrackingMap
                  customerLocation={customerLocation}
                  driverLocation={driverPos}
                  customerAddress={customerAddress}
                  distance={tracking?.distance_km ?? undefined}
                  estimatedMinutes={tracking?.eta_minutes ?? undefined}
                />
              </div>
            ) : (
              // Adresse jamais géocodée (saisie avant que le géocodage existe) :
              // le reste de la page — heure de passage, arrêts avant vous —
              // garde tout son sens sans carte.
              <div className="bg-white rounded-2xl shadow-lg p-10 text-center">
                <div className="text-5xl mb-3">📍</div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Carte indisponible</h2>
                <p className="text-gray-600">
                  Votre adresse n'a pas pu être placée sur la carte. Le suivi ci-contre reste valable.
                </p>
              </div>
            )}
          </div>

          {/* Informations commande (1/3 de l'espace) */}
          <div className="space-y-6">
            {/* Statut de livraison */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4">📦 Statut</h3>
              <div className="space-y-3">
                {order.status === 'confirmed' && (
                  <div className="text-blue-600">✓ Commande confirmée</div>
                )}
                {order.status === 'preparing' && (
                  <div className="text-purple-600">📦 En préparation</div>
                )}
                {order.status === 'ready' && (
                  <div className="text-indigo-600">✅ Prête pour livraison</div>
                )}
                {order.status === 'delivering' && (
                  <div className="text-cyan-600 font-bold text-lg">🚚 En cours de livraison</div>
                )}
                {order.status === 'completed' && (
                  <div className="text-green-700 font-bold text-lg">🎉 Livrée</div>
                )}
              </div>

              {/* Position connue : distance et temps restant. */}
              {!livree && tracking?.eta_minutes != null && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="text-3xl font-bold text-blue-600 mb-1">
                    {tracking.eta_minutes} min
                  </div>
                  <div className="text-sm text-gray-600">Temps estimé d'arrivée</div>
                  {tracking.distance_km != null && (
                    <div className="text-sm text-gray-600 mt-1">
                      Distance : {tracking.distance_km.toFixed(1)} km
                    </div>
                  )}
                  {fraicheur(tracking.driver_position_at) && (
                    <div className="text-xs text-gray-400 mt-1">
                      Position mise à jour {fraicheur(tracking.driver_position_at)}
                    </div>
                  )}
                </div>
              )}

              {/* Pas encore de position : le rang dans la tournée est ce qu'on
                  peut dire de plus concret. */}
              {!livree && tracking?.driver_position == null && tracking?.stops_before != null && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="text-3xl font-bold text-blue-600 mb-1">
                    {tracking.stops_before === 0
                      ? 'Vous êtes le prochain'
                      : `${tracking.stops_before} arrêt${tracking.stops_before > 1 ? 's' : ''} avant vous`}
                  </div>
                  <div className="text-sm text-gray-600">
                    {enRoute ? 'Le livreur est parti' : 'La tournée n’a pas encore démarré'}
                  </div>
                </div>
              )}

              {!livree && heurePrevue && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-600">Passage estimé</div>
                  <div className="text-2xl font-bold text-gray-900">vers {heurePrevue}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    Estimation : elle bouge avec la tournée.
                  </div>
                </div>
              )}
            </div>

            {/* Adresse de livraison */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4">📍 Adresse de livraison</h3>
              <div className="text-gray-700">
                <div>{order.delivery_address?.street}</div>
                <div>
                  {order.delivery_address?.postal_code} {order.delivery_address?.city}
                </div>
                {order.delivery_address?.additional_info && (
                  <div className="text-sm text-gray-600 mt-2">
                    {order.delivery_address.additional_info}
                  </div>
                )}
              </div>

              {order.delivery_date && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-600">Date prévue</div>
                  <div className="font-bold">{order.delivery_date}</div>
                  {order.delivery_slot && (
                    <div className="text-sm text-gray-600">{order.delivery_slot}</div>
                  )}
                </div>
              )}
            </div>

            {/* Détails commande */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4">🛒 Détails</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Articles</span>
                  <span className="font-bold">{order.lines.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Sous-total</span>
                  <span>{(Number(order.subtotal_cents) / 100).toFixed(2)} €</span>
                </div>
                {order.delivery_fee_cents > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Frais de livraison</span>
                    <span>{(Number(order.delivery_fee_cents) / 100).toFixed(2)} €</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                  <span>Total</span>
                  <span className="text-green-700">
                    {(Number(order.total_cents) / 100).toFixed(2)} €
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
